import {
  IWoocommerceProduct,
  IWoocommerceVariation,
  IWoocommerceVariationAttributeType,
  ProductType,
  WoocommerceProductAttributeType,
  WoocommerceProductCategoryType,
  WoocommerceProductDefaultAttributeType,
  WoocommerceProductDimensionType,
  WoocommerceProductImageType,
  WoocommerceProductMetadataType,
  WoocommerceProductTagType,
} from "interfaces";

function decodeHtmlEntities(value: string): string {
  const textarea = document.createElement("textarea");
  textarea.innerHTML = value;
  return textarea.value;
}

function mapStoreApiPrice(prices: any, field: string): string {
  const rawPrice = prices?.[field];
  if (rawPrice === undefined || rawPrice === null || rawPrice === "") {
    return "";
  }

  const minorUnit = Number(prices.currency_minor_unit ?? 0);
  return (Number(rawPrice) / Math.pow(10, minorUnit)).toString();
}

function mapStoreApiImage(image: any): WoocommerceProductImageType {
  return {
    id: image.id,
    date_created: "",
    date_created_gmt: "",
    date_modified: "",
    date_modified_gmt: "",
    src: image.src,
    name: image.name,
    alt: image.alt,
  };
}

function mapVariationAttributes(attributes: any[] = []): IWoocommerceVariationAttributeType[] {
  return attributes.map(attribute => ({
    id: attribute.id ?? 0,
    name: attribute.name,
    option: attribute.option ?? attribute.value ?? "",
  }));
}

export function mapObjectToIWoocommerceVariation(
  variation: any,
  variationSummaries: any[] = [],
): IWoocommerceVariation {
  const summary = variationSummaries.find(item => item.id === variation.id);
  const attributes = mapVariationAttributes(
    variation.attributes?.length > 0 ? variation.attributes : summary?.attributes,
  );
  const findOption = (name: string) =>
    attributes.find(attr => attr.name.toLowerCase() === name)?.option ?? "";

  return {
    id: variation.id,
    price: mapStoreApiPrice(variation.prices, "price"),
    regularPrice: mapStoreApiPrice(variation.prices, "regular_price"),
    salePrice: mapStoreApiPrice(variation.prices, "sale_price"),
    stockStatus: variation.is_in_stock ? "instock" : "outofstock",
    image: variation.images?.[0] ? mapStoreApiImage(variation.images[0]) : variation.image,
    attributes,
    size: findOption("size"),
    color: findOption("color"),
    boardType: findOption("boardtype"),
    lineLength: findOption("linelength"),
  };
}

export function mapObjectToIWoocommerceProduct(product: any): IWoocommerceProduct {
  const attributes = (product.attributes ?? []).map((attribute: any, position: number) => ({
    id: attribute.id,
    name: attribute.name,
    position,
    visible: true,
    variation: attribute.has_variations,
    options: (attribute.terms ?? []).map((term: any) => term.name),
  } as WoocommerceProductAttributeType));
  const defaultAttributes = (product.attributes ?? []).flatMap((attribute: any) =>
    (attribute.terms ?? [])
      .filter((term: any) => term.default === true)
      .map((term: any) => ({
        id: attribute.id,
        name: attribute.name,
        option: term.name,
      } as WoocommerceProductDefaultAttributeType)),
  );
  const variationAttributesById = Object.fromEntries(
    (product.variations ?? []).map((variation: any) => [
      variation.id,
      mapVariationAttributes(variation.attributes),
    ]),
  );

  return {
    id: product.id as number,
    metaData: [] as WoocommerceProductMetadataType[],
    yoast: "",
    categories: product.categories as WoocommerceProductCategoryType[],
    name: decodeHtmlEntities(product.name as string),
    price: mapStoreApiPrice(product.prices, "price"),
    regularPrice: mapStoreApiPrice(product.prices, "regular_price"),
    salePrice: mapStoreApiPrice(product.prices, "sale_price"),
    slug: product.slug as string,
    stockStatus: product.is_in_stock ? "instock" : "outofstock",
    variantIds: (product.variations ?? []).map((variation: any) => variation.id),
    tags: product.tags as WoocommerceProductTagType[],
    purchasable: product.is_purchasable as boolean,
    relatedProductIds: [],
    groupedProductIds: (product.grouped_products ?? []) as number[],
    attributes,
    defaultAttributes,
    description: product.description as string,
    dimensions: product.dimensions as WoocommerceProductDimensionType,
    images: (product.images ?? []).map(mapStoreApiImage),
    productVariations: product.productVariations ?? [],
    variationAttributesById,
    selectedProductVariation: null,
    selectedSize: "",
    selectedColor: "",
    selectedBoardType: "",
    selectedLineLength: "",
    selectionId: 0,
    productType: getProductTypeByTags(product.tags) ?? undefined,
  };
}

export function getProductTypeByTag(tag: WoocommerceProductTagType): ProductType | null {
  if (tag == null) {
    return null;
  }

  if (tag.name === "calc__kite") {
    return ProductType.Kite;
  }
  if (tag.name === "calc__board") {
    return ProductType.Board;
  }
  if (tag.name === "calc__bar") {
    return ProductType.Bar;
  }
  return null;
}

export function getProductTypeByTags(tags: WoocommerceProductTagType[] = []): ProductType | null {
  for (const tag of tags) {
    const productType = getProductTypeByTag(tag);
    if (productType !== null) {
      return productType;
    }
  }

  return null;
}
